import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { GroupMemberInfo } from '../types/accountSync';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';
import { UI_TEXTS } from '../config/accountSyncConfig';

interface LinkedPhonesListProps {
  groupMembers: GroupMemberInfo[];
  currentPhone: string;
  isSuperMaster: boolean;
  onRemove: (phone: string) => Promise<void>;
  loading?: boolean;
}

const LinkedPhonesList: React.FC<LinkedPhonesListProps> = ({
  groupMembers,
  currentPhone,
  isSuperMaster,
  onRemove,
  loading = false,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [phoneToDelete, setPhoneToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 삭제 확인 다이얼로그 열기
  const handleDeleteClick = (phone: string) => {
    setPhoneToDelete(phone);
    setDeleteConfirmOpen(true);
    setError(null);
  };

  // 삭제 실행
  const handleDeleteConfirm = async () => {
    if (!phoneToDelete) return;

    setDeleting(true);
    setError(null);

    try {
      await onRemove(phoneToDelete);
      setDeleteConfirmOpen(false);
      setPhoneToDelete(null);
    } catch (err: any) {
      setError(err.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  // 삭제 취소
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setPhoneToDelete(null);
    setError(null);
  };

  // 현재 폰을 제외한 그룹 멤버
  const otherMembers = groupMembers.filter(
    member => member.phoneNumber !== currentPhone
  );

  console.log('LinkedPhonesList Debug:', {
    groupMembers,
    currentPhone,
    otherMembers,
    groupMembersLength: groupMembers.length,
    otherMembersLength: otherMembers.length
  });

  if (loading) {
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (otherMembers.length === 0) {
    return null;
  }

  return (
    <>
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              {UI_TEXTS.LABEL_LINKED_PHONES}
            </Typography>
            <Chip 
              label={`${otherMembers.length}개`} 
              size="small" 
              sx={{ ml: 1 }} 
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          <List>
            {otherMembers.map((member, index) => (
              <ListItem
                key={member.phoneNumber}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" fontWeight="bold">
                        {member.username}
                      </Typography>
                      {member.isPrimary && (
                        <Chip 
                          label="주계정" 
                          size="small" 
                          color="primary" 
                          sx={{ ml: 1 }} 
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="body2">
                          {formatPhoneNumber(member.phoneNumber)}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          만료일: {member.expiredate}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        가입일: {new Date(member.joinedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                />
                {isSuperMaster && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteClick(member.phoneNumber)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>추가폰 삭제</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            {UI_TEXTS.CONFIRM_DELETE}
          </Typography>
          {phoneToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              삭제할 전화번호: {formatPhoneNumber(phoneToDelete)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {UI_TEXTS.BUTTON_CANCEL}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : UI_TEXTS.BUTTON_DELETE}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkedPhonesList;